'use client';

import React, {
  useRef,
  useState,
  useEffect,
  FC,
  PropsWithChildren,
  useMemo,
} from 'react';
import { motion, useMotionValue } from 'framer-motion';
import { css } from '@bts-web/utils-style-engine';
import { debounce } from '@bts-web/utils-functions';
import { Language, RTL_LANGUAGES } from '@bts-web/utils-lokalise';
import { useClientUserInfo } from '../../userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';

// TODO: Fix Unnecessary Re-renders:
// Every time you start or stop dragging, your current code triggers a React State update (setIsDragging).
const ScrollableWrapper: FC<PropsWithChildren> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const contentRef = useRef<HTMLDivElement | null>(null);

  const x = useMotionValue(0);

  const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 });

  const [isDragging, setIsDragging] = useState(false);

  const { language } = useClientUserInfo();

  const isRTL = useMemo(
    () => RTL_LANGUAGES.includes(language as Language),
    [language],
  );

  const childArray = React.Children.toArray(children);

  const updateConstraints = () => {
    if (containerRef.current && contentRef.current) {
      const containerWidth = containerRef.current.offsetWidth;

      const contentWidth = contentRef.current.scrollWidth;

      if (contentWidth > containerWidth) {
        if (isRTL) {
          setDragConstraints({
            left: 0,
            right: contentWidth - containerWidth,
          });
        } else {
          setDragConstraints({
            left: -(contentWidth - containerWidth),
            right: 0,
          });
        }
      } else {
        setDragConstraints({ left: 0, right: 0 });
      }
    }
  };

  const debouncedUpdateConstraints = debounce(updateConstraints, 1000);

  useEffect(() => {
    updateConstraints();

    window.addEventListener('resize', debouncedUpdateConstraints);

    const timer = setTimeout(updateConstraints, 100);

    return () => {
      window.removeEventListener('resize', debouncedUpdateConstraints);

      clearTimeout(timer);
    };
  }, [children]);

  useEffect(() => {
    x.set(0);
  }, [language]);

  const handleDragStart = () => setIsDragging(true);

  const handleDragEnd = () => {
    setTimeout(() => setIsDragging(false), 100);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isDragging) {
      e.stopPropagation();

      e.preventDefault();
    }
  };

  return (
    <div
      ref={containerRef}
      className={css({
        overflow: 'hidden',
        width: '100%',
      })}
      style={{ cursor: 'grab' }}
    >
      <motion.div
        ref={contentRef}
        drag="x"
        dragConstraints={dragConstraints}
        dragElastic={0.2}
        dragMomentum={true}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={handleClick}
        style={{ x }}
        whileDrag={{ cursor: 'grabbing' }}
        className={css({
          display: 'flex',
          gap: 'small',
        })}
      >
        {childArray.map((child, index) => (
          <React.Fragment key={index}>{child}</React.Fragment>
        ))}
      </motion.div>
    </div>
  );
};

export { ScrollableWrapper };
